import React, { Fragment, useMemo, useContext } from 'react'
import { Form, Input, Select, Typography } from 'antd'
import type { FormInstance } from 'antd'
import { CardElement } from '@stripe/react-stripe-js'

import { bareSubdivisionCode, countriesForPicker, findCountry, subdivisionOptions, subdivisionTermKey } from '@vacationtracker/shared/data/countries'
import IntlMessages from '../../util/IntlMessages'
import { filterOptions } from '../../util/filter-options'
import { filterCountryOptions } from '@vacationtracker/shared/functions/filter-countries'
import { ThemeContext } from '../../context/themeContext'
import { DARK_THEME } from '@vacationtracker/shared/constants/ThemeSetting'
import { SubscriptionPlan } from '@vacationtracker/shared/types/company'

const { Option } = Select
const { Title } = Typography

// Only these two ever collected a subdivision for billing. Other countries now carry subdivision
// data for holidays, which must not turn into a new required field on the payment form.
export const BILLING_SUBDIVISION_COUNTRIES = ['US', 'CA']

interface IBillingForm {
  form: FormInstance
  showPayment?: boolean
  disabled?: boolean
  currentPlan?: SubscriptionPlan
  onCountryChange?: (countryIso: string) => void
}

const BillingForm = ({ form, showPayment = true, disabled = false, currentPlan, onCountryChange }: IBillingForm): React.ReactElement => {
  const { theme } = useContext(ThemeContext)
  const watchedCountry = Form.useWatch('country', form)
  const selectedCountry = useMemo(() => findCountry(watchedCountry), [watchedCountry])

  // Add CSS for autofill styling in dark mode
  React.useEffect(() => {
    if (theme === DARK_THEME) {
      const style = document.createElement('style')
      style.textContent = `
        .dark-theme .StripeElement input:-webkit-autofill,
        .dark-theme .StripeElement input:-webkit-autofill:hover,
        .dark-theme .StripeElement input:-webkit-autofill:focus {
          -webkit-text-fill-color: #fff !important;
          -webkit-box-shadow: 0 0 0px 1000px transparent inset !important;
          transition: background-color 5000s ease-in-out 0s;
        }
      `
      document.head.appendChild(style)
      return () => {
        document.head.removeChild(style)
      }
    }
  }, [theme])

  const handleCountryChange = (countryIso: string) => {
    form.setFieldsValue({ state: '' })
    onCountryChange?.(countryIso)
  }

  return (
    <Fragment>
      <Form.Item
        className="wrap-label"
        label={<IntlMessages id="general.companyName" />}
        name="billingName"
        rules={[{ required: true, message: <IntlMessages id="components.billingForm.companyNameValidationRequired" /> }]}
      >
        <Input disabled={disabled} />
      </Form.Item>
      <Form.Item
        className="wrap-label"
        label={<IntlMessages id="components.billing.email" />}
        name="billingEmails"
        rules={[
          {
            required: true,
            type: 'array',
            message: <IntlMessages id="components.billing.emailsValidationRequired" />,
            defaultField: { type: 'email', message: <IntlMessages id="components.billing.emailsValidationRequired" /> },
          },
        ]}
      >
        <Select mode="tags" disabled={disabled} style={{ width: '100%' }} placeholder={<IntlMessages id="components.billing.emailPlaceholder" />} />
      </Form.Item>
      <Form.Item
        className="wrap-label"
        label={<IntlMessages id="components.billingForm.city" />}
        name="city"
        rules={[{ required: true, message: <IntlMessages id="components.billingForm.cityValidationRequired" /> }]}
      >
        <Input disabled={disabled} />
      </Form.Item>
      <Form.Item
        className="wrap-label"
        label={<IntlMessages id="app.country" />}
        name="country"
        rules={[{ required: true, message: <IntlMessages id="components.billingForm.countryValidationRequired" /> }]}
      >
        <Select onChange={handleCountryChange} disabled={disabled} filterOption={filterCountryOptions} showSearch>
          {countriesForPicker().map((country) => (
            <Option value={country.iso} key={country.iso}>
              {country.name}
            </Option>
          ))}
        </Select>
      </Form.Item>
      {selectedCountry && BILLING_SUBDIVISION_COUNTRIES.includes(selectedCountry.iso) && (
        <Form.Item
          className="wrap-label"
          label={<IntlMessages id={subdivisionTermKey(selectedCountry.iso)} />}
          name="state"
          rules={[{ required: true, message: <IntlMessages id="components.billingForm.stateValidationRequired" /> }]}
        >
          <Select filterOption={filterOptions} disabled={disabled} showSearch>
            {subdivisionOptions(selectedCountry.iso).map((subdivision) => (
              // Lowercase bare code, not the full ISO code: this is what Stripe's address.state and
              // the tax lookup have always held, so already-saved values keep matching an option.
              <Option value={bareSubdivisionCode(subdivision.value)?.toLowerCase()} key={subdivision.value}>
                {subdivision.label}
              </Option>
            ))}
          </Select>
        </Form.Item>
      )}
      <Form.Item
        className="wrap-label"
        label={<IntlMessages id="components.billingForm.address1" />}
        name="address"
        rules={[{ required: true, message: <IntlMessages id="components.billingForm.address1ValidationRequired" /> }]}
      >
        <Input disabled={disabled} />
      </Form.Item>
      <Form.Item className="wrap-label" label={<IntlMessages id="components.billingForm.address2" />} name="addressLine2" rules={[{ required: false }]}>
        <Input disabled={disabled} />
      </Form.Item>

      {showPayment && (
        <Title level={4}>
          <IntlMessages id="billing.paymentInformation" />
        </Title>
      )}

      {showPayment && (
        <Form.Item
          label={<IntlMessages id="app.creditCard" />}
          name="cc"
          rules={[{ required: true, message: <IntlMessages id="components.billingForm.ccRequired" /> }]}
          className="stripe-input wrap-label"
          extra={currentPlan === 'Freemium' ? '' : <IntlMessages id="components.billingForm.description" />}
        >
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  lineHeight: '24px',
                  color: theme === DARK_THEME ? '#fff' : '#545454',
                  iconColor: theme === DARK_THEME ? '#fff' : '#545454',
                  '::placeholder': {
                    color: theme === DARK_THEME ? 'rgba(255, 255, 255, 0.5)' : 'rgba(84, 84, 84, 0.5)',
                  },
                  ':-webkit-autofill': {
                    color: theme === DARK_THEME ? '#fff' : '#545454',
                  },
                },
              },
            }}
          />
        </Form.Item>
      )}
    </Fragment>
  )
}

export default BillingForm
